/**
 * Alfie-plan capability gating — data + route guard (NO React imports).
 *
 * Resolves the current viewer's *effective* Alfie feature codes (e.g.
 * `alfie_chat`, `community`) for an org. Effective = the org's Alfie plan
 * (any `organization_alfie_subscriptions` row in status active/past_due,
 * union of its `alfie_tiers.content_access_codes` across ALL qualifying rows)
 * UNION a per-user fallback: super_admins, users holding an active or
 * admin-overridden K&C community membership, and holders of an active, unexpired
 * community-tier add-on grant (WS3) are additionally granted the app feature
 * codes, matching what server-side RLS already lets them read. This is
 * NOT a mirror of any single RPC — it reconstructs the union of grants the
 * server honours so a client gate never padlocks someone the server would let in.
 *
 * This module is imported by route files (`beforeLoad`), so it must stay free of
 * React / context imports — pulling a context module in here creates an import
 * cycle back through the routes that collapses their inferred types. The React
 * hook lives in `use-alfie-capabilities.ts`.
 *
 * Reads THROW on error — a read that errors must not be mistaken for "no
 * capabilities". Throwing (a) re-activates the route guard's fail-open catch so a
 * blip can't strand an entitled user at /checkout, (b) keeps react-query from
 * caching an empty result, and (c) lets the sidebar hook treat the error as
 * "unknown" (no padlock, no upsell) rather than "Free".
 */
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { supabase, getSessionUser } from '@/lib/supabase';
import { getCurrentOrganization } from '@/hooks/use-bss-organizations';

export const ALFIE_CAP_STALE_MS = 5 * 60 * 1000;

/**
 * App feature codes granted to super_admins and to any user holding an
 * active/overridden K&C community membership. Such users must never be
 * padlocked: server-side RLS already grants them these surfaces, and every K&C
 * tier bundles an Alfie plan whose `content_access_codes` include both codes.
 */
const FALLBACK_CODES = ['community', 'alfie_chat', 'speedies'] as const;

export function alfieCapabilitiesKey(orgId: string) {
  return ['alfie-capabilities', orgId] as const;
}

/**
 * Union the org-plan feature codes across every qualifying subscription row.
 * The embedded `alfie_tiers` is a to-one relation (an object at runtime), but
 * the untyped client widens it to an array — cast via unknown and normalise.
 */
function planCodesFromRows(data: unknown): string[] {
  const rows = (data ?? []) as unknown as Array<{
    alfie_tiers:
      | { content_access_codes: string[] | null }
      | { content_access_codes: string[] | null }[]
      | null;
  }>;
  const codes: string[] = [];
  for (const row of rows) {
    const embedded = Array.isArray(row.alfie_tiers) ? row.alfie_tiers[0] : row.alfie_tiers;
    for (const code of embedded?.content_access_codes ?? []) codes.push(code);
  }
  return codes;
}

/**
 * Effective feature codes the current viewer has for `orgId`: the union of the
 * org's active/past_due Alfie plan codes plus the super_admin / membership
 * fallback. Returns a deduped list. THROWS if any read errors (see module doc).
 */
export async function fetchAlfieCapabilities(orgId: string): Promise<string[]> {
  const userId = (await getSessionUser())?.id ?? null;

  // Org plan: ALL qualifying rows (no order/limit/single) so codes union across
  // rows — this fixes the newest-row-wins bug and adds past_due to the gate.
  const planQuery = supabase
    .from('organization_alfie_subscriptions')
    .select('alfie_tiers(content_access_codes)')
    .eq('organization_id', orgId)
    .in('status', ['active', 'past_due']);

  // No session user → org plan only, no per-user fallback reads.
  if (!userId) {
    const planResult = await planQuery;
    if (planResult.error) throw new Error(planResult.error.message);
    return Array.from(new Set(planCodesFromRows(planResult.data)));
  }

  const nowIso = new Date().toISOString();
  const [planResult, roleResult, membershipResult, addonResult, memberResult] = await Promise.all([
    planQuery,
    supabase.from('global_user_roles').select('role').eq('user_id', userId).maybeSingle(),
    // A GRANTING, non-expired community membership. This mirrors the server's
    // authoritative `get_user_tier` / `user_has_tier_access` (mig 475), which
    // read `status IN ('active','past_due') AND (expires_at IS NULL OR
    // expires_at > now())` — past_due is the dunning grace window and must not
    // padlock a member mid-retry. `tier_override` is only a LEVEL modifier on a
    // granting row server-side — a leftover override on a cancelled/expired
    // membership grants nothing there, so it must not grant here either.
    supabase
      .from('user_community_memberships')
      .select('user_id')
      .eq('user_id', userId)
      .in('status', ['active', 'past_due'])
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
      .limit(1),
    // An active, unexpired add-on grant for a community TIER is a comped seat
    // (WS3, contract 3.4ii): the holder gets the same app feature codes a paid
    // seat does. RLS (`addon_grants_select`, mig 307) admits `user_id =
    // auth.uid()` for a user-scoped grant OR the caller's active orgs for an
    // org-scoped grant, so this one read covers both without an org filter here.
    // Package grants (community_tier_id NULL) are content-only — excluded.
    supabase
      .from('addon_grants')
      .select('id')
      .not('community_tier_id', 'is', null)
      .eq('status', 'active')
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
      .limit(1),
    // The org's plan codes apply to this viewer only while they hold an ACTIVE
    // membership on THIS org (a suspended member keeps the org context but not
    // the plan). Mirrors the server: `userHasAlfieFeature` in the chat edge
    // function and `user_has_alfie_feature` arm 2 (hermes mig 587) both filter
    // `organization_members.status = 'active'`. Membership status only — the
    // subscription filter above keeps past_due on purpose.
    supabase
      .from('organization_members')
      .select('status')
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .limit(1),
  ]);

  if (planResult.error) throw new Error(planResult.error.message);
  if (roleResult.error) throw new Error(roleResult.error.message);
  if (membershipResult.error) throw new Error(membershipResult.error.message);
  if (addonResult.error) throw new Error(addonResult.error.message);
  if (memberResult.error) throw new Error(memberResult.error.message);

  const activeMember = ((memberResult.data as unknown[] | null)?.length ?? 0) > 0;
  const codes = new Set(activeMember ? planCodesFromRows(planResult.data) : []);

  const role = (roleResult.data as { role?: string } | null)?.role;
  const superAdmin = role === 'super_admin';
  const hasMembership = ((membershipResult.data as unknown[] | null)?.length ?? 0) > 0;
  const hasAddon = ((addonResult.data as unknown[] | null)?.length ?? 0) > 0;
  if (superAdmin || hasMembership || hasAddon) {
    for (const code of FALLBACK_CODES) codes.add(code);
  }

  return Array.from(codes);
}

/**
 * Route guard: redirect to `/checkout` unless the viewer's effective Alfie caps
 * grant `feature`. Shares the capability cache with the sidebar hook. Fails OPEN
 * on any read/resolution error so a blip can't strand an entitled user.
 */
export async function requireAlfieCapability(
  queryClient: QueryClient,
  feature: string,
): Promise<void> {
  let orgId: string | undefined;
  try {
    const orgData = await queryClient.ensureQueryData({
      queryKey: ['current-organization'],
      queryFn: getCurrentOrganization,
      staleTime: ALFIE_CAP_STALE_MS,
      // Same PROMPT fail-open as the capability read below. The global retry
      // policy retries 5xx twice on a 1s/2s backoff, and `beforeLoad` blocks
      // the whole navigation while it does — the previous page just sits there
      // frozen for ~3s. Those retries buy nothing: the catch below fails open
      // either way, so the only thing they change is how long the user waits.
      retry: false,
    });
    orgId = (orgData as { organization?: { id?: string } } | null)?.organization?.id;
  } catch {
    return; // org resolution failure → let the root guard handle it
  }
  if (!orgId) return; // no org → root guard sends to onboarding

  let caps: string[];
  try {
    caps = await queryClient.ensureQueryData({
      queryKey: alfieCapabilitiesKey(orgId),
      queryFn: () => fetchAlfieCapabilities(orgId as string),
      staleTime: ALFIE_CAP_STALE_MS,
      // Fail open PROMPTLY: a failing read must not hang beforeLoad through the
      // global retry/backoff. The catch below turns the (un-cached) error into
      // "let them in".
      retry: false,
    });
  } catch {
    return; // fail open — a read blip must not lock a paying user out
  }

  if (!caps.includes(feature)) {
    throw redirect({ to: '/checkout' });
  }
}
